import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { TrendingUp, TrendingDown, Minus, CalendarRange, ArrowRight } from "lucide-react";

export default function PaceStrategy({ propId, lang = "it", onPickDate }) {
  const L = (it, en) => (lang === "it" ? it : en);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!propId) return;
    setLoading(true);
    api.get(`/properties/${propId}/pace-strategy?lang=${lang}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ has_data: false }))
      .finally(() => setLoading(false));
  }, [propId, lang]);

  if (loading) return null;
  if (!data || !data.has_data) return null;

  const fmt = (iso) => new Date(iso).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", { day: "2-digit", month: "short" });
  const totals = data.totals || {};

  const DayRow = ({ d, kind }) => (
    <button
      data-testid={`pace-day-${d.date}`}
      onClick={() => onPickDate && onPickDate(d.date)}
      className={`w-full flex items-center justify-between gap-3 text-left px-3 py-2 rounded-md transition-colors ${kind === "behind" ? "bg-amber-500/10 hover:bg-amber-500/20" : "bg-emerald-500/10 hover:bg-emerald-500/20"}`}
    >
      <div className="min-w-0">
        <div className="text-sm font-semibold">{fmt(d.date)} <span className="opacity-60 font-normal">· {L("tra", "in")} {d.lead_time}{L("gg", "d")}</span></div>
        <div className="text-xs text-muted-foreground">
          {L("occ.", "occ.")} {d.current_occ_pct}% {L("vs", "vs")} {d.expected_occ_pct}% {L("attesa", "expected")} · {d.available}/{d.total} {L("libere", "left")}
        </div>
      </div>
      <div className={`flex items-center gap-1 shrink-0 text-sm font-bold ${kind === "behind" ? "text-amber-600 dark:text-amber-400" : "text-emerald-600 dark:text-emerald-400"}`}>
        {kind === "behind" ? <TrendingDown className="w-4 h-4" /> : <TrendingUp className="w-4 h-4" />}
        {d.gap_pts > 0 ? "+" : ""}{d.gap_pts}pt
        <ArrowRight className="w-3.5 h-3.5 opacity-50" />
      </div>
    </button>
  );

  return (
    <motion.div data-testid="pace-strategy" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="md:col-span-4 bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <CalendarRange className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-semibold">{L("Strategia Ritmo Vendite", "Sales Pace Strategy")}</h3>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400"><TrendingUp className="w-3.5 h-3.5" /> {totals.ahead || 0} {L("in anticipo", "ahead")}</span>
          <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400"><TrendingDown className="w-3.5 h-3.5" /> {totals.behind || 0} {L("in ritardo", "behind")}</span>
          <span className="flex items-center gap-1 text-muted-foreground"><Minus className="w-3.5 h-3.5" /> {totals.on_track || 0} {L("in linea", "on track")}</span>
        </div>
      </div>

      {/* month bars */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-5" data-testid="pace-months">
        {(data.months || []).map((m) => {
          const tot = Math.max(1, m.ahead + m.behind + m.on_track);
          return (
            <div key={m.month} className="bg-secondary/40 rounded-md p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold">{m.label}</span>
                <span className={`text-xs font-bold ${m.avg_gap_pts >= 8 ? "text-emerald-600 dark:text-emerald-400" : m.avg_gap_pts <= -8 ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground"}`}>
                  {m.avg_gap_pts > 0 ? "+" : ""}{m.avg_gap_pts}pt
                </span>
              </div>
              <div className="h-2 rounded-full overflow-hidden flex bg-muted">
                <div className="bg-emerald-500" style={{ width: `${(m.ahead / tot) * 100}%` }} />
                <div className="bg-muted-foreground/40" style={{ width: `${(m.on_track / tot) * 100}%` }} />
                <div className="bg-amber-500" style={{ width: `${(m.behind / tot) * 100}%` }} />
              </div>
              <div className="flex items-center justify-between mt-1.5 text-[11px] text-muted-foreground">
                <span className="text-emerald-600 dark:text-emerald-400">{m.ahead} ↑</span>
                <span>{m.on_track} =</span>
                <span className="text-amber-600 dark:text-amber-400">{m.behind} ↓</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <div className="text-xs uppercase tracking-wide font-bold text-amber-600 dark:text-amber-400 mb-2">
            {L("Dove intervenire subito (in ritardo)", "Act now (behind pace)")}
          </div>
          <div className="space-y-1.5" data-testid="pace-critical-behind">
            {(data.critical_behind || []).length === 0 ? (
              <div className="text-sm text-muted-foreground">{L("Nessun giorno in forte ritardo.", "No day significantly behind.")}</div>
            ) : (data.critical_behind || []).map((d) => <DayRow key={d.date} d={d} kind="behind" />)}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wide font-bold text-emerald-600 dark:text-emerald-400 mb-2">
            {L("Dove puoi alzare (in anticipo)", "Where you can raise (ahead)")}
          </div>
          <div className="space-y-1.5" data-testid="pace-opportunities">
            {(data.opportunities_ahead || []).length === 0 ? (
              <div className="text-sm text-muted-foreground">{L("Nessun giorno in forte anticipo.", "No day significantly ahead.")}</div>
            ) : (data.opportunities_ahead || []).map((d) => <DayRow key={d.date} d={d} kind="ahead" />)}
          </div>
        </div>
      </div>
      {data.as_of ? (
        <div className="text-[11px] text-muted-foreground mt-4">
          {L("Aggiornato dal tuo ultimo import:", "Updated from your last import:")} {new Date(data.as_of).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB")}
        </div>
      ) : null}
    </motion.div>
  );
}
